import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';
import PaymentPage from './pages/PaymentPage';
import AdminPortal from './portals/AdminPortal';
import CustomerPortal from './portals/CustomerPortal';
import WorkerPortal from './portals/WorkerPortal';
import HowItWorks from './components/HowItWorks';
import ContributionSection from './components/ContributionSection';
import './App.css';

const AboutSection: React.FC = () => {
  return (
    <section id="about" className="about">
      <div className="container">
        <h2 className="section-title">Acerca de World Conserve</h2>

        <div className="story-section">
          <div className="story-card">
            <div className="founders-intro">
              <div className="founder-info">
                <div className="founder-icon">
                  <img src="/images/zacsquare.JPG" alt="Zac Morris" />
                </div>
                <div>
                  <strong>Zac Morris</strong>
                  <p>Creador de Contenido</p>
                </div>
              </div>
              <div className="plus-icon">+</div>
              <div className="founder-info">
                <div className="founder-icon">
                  <img src="/images/alejo.png" alt="Alejandro Pedraza" />
                </div>
                <div>
                  <strong>Alejandro Pedraza</strong>
                  <p>Ingeniero Ambiental</p>
                </div>
              </div>
            </div>
            <div className="story-text">
              <p>World Conserve es la idea de Zac Morris y Alejandro Pedraza. Zac es un creador de contenido ampliamente reconocido en Sudamérica, y Alejandro es un ingeniero ambiental con una prolífica carrera en reforestación en Colombia.</p>
            </div>
          </div>
        </div>

        <div className="story-section">
          <div className="story-card">
            <div className="story-icon">
              <img src="/images/handshake.png" alt="Asociación" />
            </div>
            <div className="story-text enhanced-story">
              <div className="story-segments">
                {[
                  { icon: '🤝', text: 'Con el tiempo, ambos se dieron cuenta de que compartían una profunda pasión por la naturaleza y la conservación de ecosistemas.' },
                  { icon: '💡', text: 'Alejandro, quien ya trabajaba en importantes proyectos de reforestación con industrias petroleras y energéticas en Colombia, vio una oportunidad para combinar su experiencia con el alcance de Zac.' },
                  { icon: '🌳', text: 'Juntos, podrían ofrecer un producto realmente único a muchas personas: <strong class="trees-emphasis">¡Árboles!</strong> Nuestros árboles se compran como cualquier producto en internet, pero son todo lo contrario: <em class="regenerate-text">mientras otros desgastan el mundo, los árboles lo regeneran</em>.' }
                ].map((segment, index) => (
                  <div key={index} className="story-segment">
                    <span className="segment-icon">{segment.icon}</span>
                    <p className="segment-text" dangerouslySetInnerHTML={{ __html: segment.text }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mission-section">
          <div className="mission-card">
            <div className="mission-icon">💡</div>
            <div className="mission-content">
              <h3>Nuestra misión sencilla</h3>
              <div className="mission-statement-container">
                <p>Mostrarle al mundo la importancia de los árboles y darle a las personas una forma fácil de ayudar a que hayan más árboles ahora y para siempre.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="features-section">
          <h3>Beneficios de Plantar un Árbol con World Conserve</h3>
          <div className="features-grid">
            {[
              { icon: '🌱', title: 'Un Árbol Nativo', text: 'Tu árbol usa solo semillas nativas colombianas seleccionadas específicamente para el ecosistema local' },
              { icon: '📍', title: 'La Ubicación', text: 'Las coordenadas GPS exactas del árbol que plantas son enviadas directamente a ti' },
              { icon: '📸', title: 'Evidencia', text: 'Prueba fotográfica de tu árbol plantado' },
              { icon: '🛡️', title: 'Protección a Largo Plazo', text: 'Tu árbol recibe cuidado profesional durante su vida útil' },
              { icon: '💧', title: 'Contribución Hídrica', text: 'Tu árbol filtrará miles de galones de agua subterránea anualmente' },
              { icon: '🌍', title: 'Captura de Carbono', text: 'Tu árbol captura 500kg de CO₂ anualmente del aire' },
              { icon: '🎯', title: 'Un Legado', text: 'Ayuda a crear nuevos bosques en Colombia' },
              { icon: '📈', title: 'Seguimiento de Progreso', text: 'Rastrea el crecimiento de tu árbol a lo largo de su vida' },
              { icon: '🦋', title: 'Restauración de Hábitat', text: 'Tu árbol contribuye a restaurar el hábitat natural para la fauna colombiana' },
            ].map((f, i) => (
              <div key={i} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <div className="feature-content">
                  <h4>{f.title}</h4>
                  <p>{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ImpactSection: React.FC = () => {
  const [treesPlanted, setTreesPlanted] = React.useState<number>(50000);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/stats');
        if (response.ok) {
          const data = await response.json();
          setTreesPlanted(data.treesPlanted);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const formatNumber = (num: number) => {
    if (num >= 1000) return `${Math.floor(num / 1000).toLocaleString()}k+`;
    return num.toLocaleString();
  };

  const calculateHectares = (trees: number) => Math.floor(trees / 100) + 3;

  return (
    <section id="impact" className="impact">
      <div className="container">
        <h2 className="section-title">Nuestro Impacto Ambiental</h2>
        <div className="impact-grid">
          <div className="impact-card">
            <div className="impact-icon">🌳</div>
            <div className="impact-number">{isLoading ? '...' : formatNumber(treesPlanted)}</div>
            <div className="impact-label">Árboles Plantados</div>
            <p className="impact-description">Especies nativas cuidadosamente seleccionadas para la restauración de la biodiversidad de Colombia</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">🌍</div>
            <div className="impact-number">{isLoading ? '...' : `${calculateHectares(treesPlanted)}+`}</div>
            <div className="impact-label">Hectáreas Restauradas</div>
            <p className="impact-description">Tierras degradadas transformadas en ecosistemas forestales prósperos</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">💨</div>
            <div className="impact-number">25,000</div>
            <div className="impact-label">Toneladas CO₂ Absorbidas</div>
            <p className="impact-description">Contribuyendo a los esfuerzos globales de mitigación del cambio climático</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">🦋</div>
            <div className="impact-number">150+</div>
            <div className="impact-label">Especies Protegidas</div>
            <p className="impact-description">Hábitats de vida silvestre restaurados para la fauna colombiana en peligro</p>
          </div>
        </div>

        <div className="certifications-section">
          <h3>Cada Árbol que Plantas Hace una Diferencia Real</h3>
          <div className="certifications-grid">
            <div className="certification-item">
              <div className="cert-icon">💨</div>
              <div>
                <strong>500kg CO₂ Absorbidos Anualmente</strong>
                <p>Tu árbol captura media tonelada de dióxido de carbono de la atmósfera cada año</p>
              </div>
            </div>
            <div className="certification-item">
              <div className="cert-icon">💧</div>
              <div>
                <strong>48,000 Galones de Agua Filtrada</strong>
                <p>Cada árbol filtra naturalmente miles de galones de agua subterránea anualmente</p>
              </div>
            </div>
            <div className="certification-item">
              <div className="cert-icon">🏠</div>
              <div>
                <strong>Hábitat para Más de 50 Especies</strong>
                <p>Los árboles nativos proporcionan hogar para aves, insectos y mamíferos en los bosques de Colombia</p>
              </div>
            </div>
          </div>
        </div>

        <div className="impact-cta">
          <h3>¿Listo para hacer una diferencia real?</h3>
          <button
            className="btn btn-primary"
            onClick={() => document.getElementById('contribute')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            🌱 Plantar Mi Árbol Ahora
          </button>
          <p className="impact-cta-note">🔒 Pago seguro • Desde $5 USD • Certificado y GPS incluidos</p>
        </div>

        <div className="transparency-section">
          <h3>Transparencia y Responsabilidad</h3>
          <div className="transparency-features">
            <div className="feature-item">
              <div className="feature-icon">📍</div>
              <div>
                <strong>Seguimiento GPS</strong>
                <p>Cada árbol plantado está etiquetado con GPS con coordenadas exactas</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📸</div>
              <div>
                <strong>Documentación Fotográfica</strong>
                <p>Foto inicial de plantación gratuita, con paquetes de actualizaciones de por vida opcionales disponibles</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🚶‍♀️</div>
              <div>
                <strong>Visitas Opcionales al Sitio</strong>
                <p>Visita y ve tu árbol real en persona — organiza visitas guiadas a tu área forestal</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer: React.FC = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-content">
        <div className="footer-section">
          <h4>World Conserve</h4>
          <p>Restaurando los bosques de Colombia, un árbol a la vez.</p>
        </div>
        <div className="footer-section">
          <h4>Enlaces</h4>
          <ul>
            <li><a href="#home">Inicio</a></li>
            <li><a href="#about">Acerca de</a></li>
            <li><a href="/#contribute">Plantar Árboles</a></li>
            <li><a href="/customer-portal">Mis Árboles</a></li>
          </ul>
        </div>
        <div className="footer-section">
          <h4>Contacto</h4>
          <p>Email: hello@worldconserve.org</p>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2024 World Conserve. Todos los derechos reservados.</p>
      </div>
    </div>
  </footer>
);

const HomePage: React.FC = () => (
  <>
    <HeroSection />
    <AboutSection />
    <HowItWorks />
    <ContributionSection />
    <ImpactSection />
  </>
);

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<><Navigation /><main><HomePage /></main><Footer /></>} />
          <Route path="/plant-trees" element={<PaymentPage />} />
          <Route path="/admin-dashboard" element={<AdminPortal />} />
          <Route path="/admin-dashboard.html" element={<AdminPortal />} />
          <Route path="/customer-portal" element={<CustomerPortal />} />
          <Route path="/customer-portal.html" element={<CustomerPortal />} />
          <Route path="/worker-portal" element={<WorkerPortal />} />
          <Route path="/worker-portal.html" element={<WorkerPortal />} />
          <Route path="*" element={<><Navigation /><main><HomePage /></main><Footer /></>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
